import { h, vaciar, km2, mb, num } from '../dom';
import { anadir } from '../dom';
import type { Contexto } from '../contexto';
import type { Estado, Zona } from '../estado';
import { teselarBbox, teselarPoligono, estimarDescargaMb, type Tesela } from '../../geo/teselado';
import type { CapaCatastro } from '../../catastro/wfs';
import { areaBbox, areaMultiPoligono, bboxMultiPoligono, type MultiPoligono } from '../../geo/geometria';
import { proyectarAnillo, husoParaLonLat } from '../../geo/proyecciones';
import { buscarMunicipios, poligonoMunicipio, type MunicipioCandidato } from '../../catastro/cartociudad';
import { teselasHidro } from '../../ign/cargadorHidro';
import type { Progreso } from '../../db/protocolo';
import { prepararModelo, modeloListo, MARGEN_ZONA_M, MAX_KM2_MODELO } from '../../lidar/preparar';
import { claveModelo, type EstadoArea } from '../../lidar/api';

export const MAX_KM2 = 25;
/**
 * Un termino municipal se carga por las teselas de 1 km que lo tocan. En zonas rurales una tesela
 * tarda ~2 s y pesa ~200 KB (medido en Cortes de la Frontera el 2026-09-23), asi que un municipio
 * de 200 km2 son unos minutos; el limite evita cargar una capital entera por error.
 */
export const MAX_KM2_MUNICIPIO = 400;
const DIAS_VIGENCIA = 7;
const CONCURRENCIA = 6;
const CLAVE_ZONA = 'cernicalo:zona';

type CapaZona = CapaCatastro | 'hy';

/** Panel "Zona": elegir la zona (vista, rectangulo o municipio), cargar el Catastro y la hidrografia, y gestionar lo cargado. */
export class ZonaPanel {
  readonly el = h('section', { class: 'panel-contenido' });
  private capas: Set<CapaZona> = new Set(['cp', 'bu', 'hy']);
  private densidad = 0.5;
  private textoMunicipio = '';
  private candidatosMunicipio: MunicipioCandidato[] | null = null;
  private buscandoMunicipio = false;

  constructor(private readonly ctx: Contexto) {
    ctx.almacen.suscribir((e, cambios) => {
      if ('zona' in cambios) this.guardarZona(e.zona);
      if ('zona' in cambios || 'cargando' in cambios || 'resumen' in cambios || 'herramienta' in cambios || 'persistente' in cambios) this.render(e);
    });
    this.restaurarZona();
    this.render(ctx.almacen.estado);
  }

  /** Teselas de 1 km de la zona: las de la caja o, si es un municipio, solo las que tocan su limite. */
  static teselasDe(zona: Zona): Tesela[] {
    if (zona.poligono && zona.poligono.length) {
      const ids = new Set<string>();
      const salida: Tesela[] = [];
      for (const pol of zona.poligono) {
        for (const t of teselarPoligono(pol, zona.epsg)) {
          if (!ids.has(t.id)) { ids.add(t.id); salida.push(t); }
        }
      }
      return salida;
    }
    return teselarBbox(zona.bbox, zona.epsg);
  }

  static areaDe(zona: Zona): number {
    return zona.poligono && zona.poligono.length ? areaMultiPoligono(zona.poligono) : areaBbox(zona.bbox);
  }

  private render(e: Estado): void {
    vaciar(this.el);
    const zona = e.zona;
    const teselas = zona ? ZonaPanel.teselasDe(zona) : [];
    const area = zona ? ZonaPanel.areaDe(zona) : 0;
    const capasCatastro = [...this.capas].filter((c): c is CapaCatastro => c !== 'hy');
    const estimado = estimarDescargaMb(teselas.length, capasCatastro, this.densidad);
    const maximo = zona?.municipio ? MAX_KM2_MUNICIPIO : MAX_KM2;
    const demasiado = area / 1e6 > maximo;
    const grande = !demasiado && area / 1e6 > MAX_KM2;

    anadir(this.el,
      h('h2', null, '1. Zona de carga'),
      h('p', { class: 'ayuda' }, 'Elige la zona donde buscar. El Catastro se descarga por teselas de 1 km y se guarda en tu navegador durante ', String(DIAS_VIGENCIA), ' días.'),
      h('div', { class: 'fila-botones' },
        h('button', { class: 'boton', onClick: () => this.usarVista() }, 'Usar la vista actual'),
        h('button', { class: `boton ${e.herramienta === 'zona' ? 'activo' : ''}`, onClick: () => this.dibujar() }, e.herramienta === 'zona' ? 'Dibujando… (arrastra en el mapa)' : 'Dibujar rectángulo'),
      ),
      h('div', { class: 'campo-num' },
        h('label', null, 'O un municipio entero (límite de CartoCiudad)'),
        h('div', { class: 'campo-inline' },
          h('input', {
            type: 'search', class: 'medio', placeholder: 'Nombre del municipio', value: this.textoMunicipio, 'aria-label': 'Municipio',
            onInput: (ev: Event) => { this.textoMunicipio = (ev.target as HTMLInputElement).value; },
            onKeydown: (ev: KeyboardEvent) => { if (ev.key === 'Enter') void this.buscarMunicipio(); },
          }),
          h('button', { class: 'boton secundario', disabled: this.buscandoMunicipio, onClick: () => void this.buscarMunicipio() }, this.buscandoMunicipio ? 'Buscando…' : 'Buscar municipio')),
        this.candidatosMunicipio === null ? null
          : this.candidatosMunicipio.length === 0 ? h('div', { class: 'error' }, 'CartoCiudad no encontró ningún municipio con ese nombre.')
            : h('div', { class: 'lista' }, ...this.candidatosMunicipio.map((c) =>
              h('button', { class: 'item', onClick: () => void this.elegirMunicipio(c) }, `${c.nombre}${c.provincia ? ` (${c.provincia})` : ''} · INE ${c.codigo}`)))),
      zona
        ? h('div', { class: 'tarjeta' },
          zona.municipio ? h('div', null, h('strong', null, zona.municipio.nombre), zona.municipio.provincia ? ` (${zona.municipio.provincia})` : '', ` · INE ${zona.municipio.codigo}`) : null,
          h('div', null, h('strong', null, km2(area)), ` · ${teselas.length} tesela${teselas.length === 1 ? '' : 's'} · huso ${zona.epsg}`),
          demasiado ? h('div', { class: 'error' }, `Supera el máximo de ${maximo} km². Reduce la zona.`) : null,
          grande ? h('div', { class: 'silencio' }, `Más de ${MAX_KM2} km²: en zonas rurales cada tesela tarda unos 2 s, así que la carga puede llevar varios minutos.`) : null,
          h('div', { class: 'fila-botones' },
            h('button', { class: 'boton secundario', onClick: () => this.ctx.mapa.encajarBbox(zona.bbox, zona.epsg, zona.municipio ? 13 : 16) }, 'Ver en el mapa'),
            h('button', { class: 'boton secundario', onClick: () => { this.ctx.almacen.set({ zona: null }); this.ctx.mapa.mostrarZona(null); } }, 'Quitar'),
          ),
          this.seccionModelo(zona))
        : h('p', { class: 'silencio' }, 'Ninguna zona elegida.'),
      h('h3', null, 'Capas'),
      this.checkCapa('cp', 'Parcelas (obligatorio)', true),
      this.checkCapa('bu', 'Edificios: superficie construida, uso, año, viviendas'),
      this.checkCapa('bp', 'Partes de edificio: plantas por cuerpo (pesa 4 veces más; se cargan solas al ver el 3D)'),
      this.checkCapa('hy', 'Ríos y arroyos del IGN: distancia de cada parcela al agua (para buscar «cerca de un río»)'),
      h('label', { class: 'campo' }, 'Densidad de la zona ',
        h('select', {
          onChange: (ev: Event) => { this.densidad = Number((ev.target as HTMLSelectElement).value); this.render(this.ctx.almacen.estado); },
        },
        ...[[1, 'Centro de ciudad'], [0.5, 'Mixta'], [0.2, 'Chalets o pueblo']].map(([v, t]) =>
          h('option', { value: String(v), selected: this.densidad === v }, String(t))))),
      h('p', { class: 'silencio' }, zona ? `Descarga estimada: ${mb(estimado)}` : ''),
      e.cargando ? this.progreso(e) : h('button', {
        class: 'boton primario', disabled: !zona || demasiado || teselas.length === 0,
        onClick: () => this.cargar(),
      }, 'Cargar Catastro de la zona'),
      h('h3', null, 'Datos cargados'),
      this.resumen(e),
    );
  }

  private checkCapa(capa: CapaZona, texto: string, fijo = false): HTMLElement {
    return h('label', { class: 'check' },
      h('input', {
        type: 'checkbox', checked: this.capas.has(capa), disabled: fijo,
        onChange: (ev: Event) => { if ((ev.target as HTMLInputElement).checked) this.capas.add(capa); else this.capas.delete(capa); this.render(this.ctx.almacen.estado); },
      }), ' ', texto);
  }

  private static nombreCapa(capa: Progreso['capa']): string {
    return capa === 'cp' ? 'parcelas' : capa === 'bu' ? 'edificios' : capa === 'bp' ? 'partes' : capa === 'hy' ? 'ríos y arroyos' : '';
  }

  private progreso(e: Estado): HTMLElement {
    const p = e.cargando!;
    const pct = p.total ? Math.round((p.hechas / p.total) * 100) : 0;
    return h('div', { class: 'tarjeta' },
      h('div', { class: 'barra' }, h('div', { class: 'barra-relleno', style: `width:${pct}%` })),
      h('div', null, `${p.hechas} de ${p.total} teselas (${pct} %)`, p.capa ? ` · ${ZonaPanel.nombreCapa(p.capa)}` : ''),
      p.errores.length ? h('div', { class: 'error' }, `${p.errores.length} error(es): ${p.errores[p.errores.length - 1]}`) : null,
      h('button', { class: 'boton secundario', onClick: () => this.ctx.db.cancelarCarga() }, 'Cancelar'),
    );
  }

  onAvanzado: (() => void) | null = null;
  onExportarDxf: (() => void) | null = null;

  private resumen(e: Estado): HTMLElement {
    const r = e.resumen;
    if (!r || r.parcelas === 0) return h('p', { class: 'silencio' }, 'Todavía no hay datos. Carga una zona para poder buscar.');
    return h('div', { class: 'tarjeta' },
      h('div', null, `${num(r.parcelas)} parcelas · ${num(r.edificios)} edificios · ${num(r.partes)} partes · ${num(r.inmuebles)} inmuebles · ${num(r.cursosAgua)} cursos de agua`),
      h('div', { class: 'silencio' }, `${num(r.teselas)} teselas cargadas · ${e.persistente === false ? 'solo en memoria (sin OPFS)' : 'guardado en el navegador'}`),
      h('div', { class: 'fila-botones' },
        r.bbox ? h('button', { class: 'boton secundario', onClick: () => this.ctx.mapa.encajarBbox(r.bbox!, r.epsg ?? 25830, 15) }, 'Ver todo lo cargado') : null,
        h('button', { class: 'boton secundario', onClick: () => this.onAvanzado?.() }, 'SQL y exportar'),
        h('button', { class: 'boton secundario', onClick: () => this.onExportarDxf?.() }, e.zona && !e.zona.poligono ? 'DXF de la zona' : 'DXF de la vista'),
        h('button', { class: 'boton secundario', onClick: () => this.vaciar(false) }, 'Vaciar Catastro'),
        h('button', { class: 'boton peligro', onClick: () => this.vaciar(true) }, 'Vaciar todo'),
      ),
    );
  }

  private usarVista(): void {
    const { bbox, epsg } = this.ctx.mapa.bboxVistaUtm();
    this.ctx.almacen.set({ zona: { bbox, epsg }, herramienta: 'ninguna' });
    this.ctx.mapa.setHerramienta('ninguna');
    this.ctx.mapa.mostrarZona({ bbox, epsg });
  }

  private dibujar(): void {
    const activa = this.ctx.almacen.estado.herramienta === 'zona';
    this.ctx.almacen.set({ herramienta: activa ? 'ninguna' : 'zona' });
    this.ctx.mapa.setHerramienta(activa ? 'ninguna' : 'zona');
  }

  // ---------- Modelo LiDAR del rectangulo ----------

  private modeloEstado: EstadoArea | null = null;
  private modeloClave: string | null = null;
  private modeloOcupado = false;
  private modeloControl: AbortController | null = null;

  /** Solo para rectangulos (no municipios) de hasta MAX_KM2_MODELO con el margen incluido. */
  static admiteModelo(zona: Zona): boolean {
    if (zona.poligono && zona.poligono.length) return false;
    const [x0, y0, x1, y1] = zona.bbox;
    return ((x1 - x0 + 2 * MARGEN_ZONA_M) * (y1 - y0 + 2 * MARGEN_ZONA_M)) / 1e6 <= MAX_KM2_MODELO;
  }

  private seccionModelo(zona: Zona): HTMLElement | null {
    if (zona.poligono && zona.poligono.length) return null;
    if (!ZonaPanel.admiteModelo(zona)) return h('div', { class: 'silencio' }, `El modelo LiDAR de la zona admite rectángulos de hasta ${MAX_KM2_MODELO} km² (con ${MARGEN_ZONA_M} m de margen).`);
    const clave = claveModelo({ bbox: zona.bbox, epsg: zona.epsg });
    if (this.modeloClave !== clave) {
      this.modeloClave = clave;
      this.modeloEstado = null;
      void modeloListo(clave).then((e) => { if (e && this.modeloClave === clave && !this.modeloOcupado) { this.modeloEstado = e; this.render(this.ctx.almacen.estado); } });
    }
    const e = this.modeloEstado;
    const listo = e?.estado === 'listo' && !!e.productos['modelo.json'];
    const pct = e && e.hojasTotal ? Math.round((e.hojasHechas / e.hojasTotal) * 100) : 0;
    return h('div', null,
      this.modeloOcupado && e
        ? h('div', null,
          h('div', { class: 'barra' }, h('div', { class: 'barra-relleno', style: `width:${pct}%` })),
          h('div', { class: 'silencio' }, e.mensaje ?? e.estado, e.hojasTotal ? ` · ${e.hojasHechas}/${e.hojasTotal} hojas` : ''),
          h('button', { class: 'boton secundario', onClick: () => this.modeloControl?.abort() }, 'Dejar de esperar'))
        : h('div', { class: 'fila-botones' },
          h('button', { class: 'boton secundario', disabled: this.modeloOcupado, title: `Cubiertas, muros, vegetación, MDT 50 cm y curvas del rectángulo y ${MARGEN_ZONA_M} m alrededor`, onClick: () => void this.prepararModelo(zona) }, listo ? 'Recalcular modelo LiDAR' : 'Modelo LiDAR de la zona'),
          listo ? h('button', { class: 'boton primario', onClick: () => void this.verModelo(zona) }, 'Ver 3D') : null),
      e?.estado === 'error' ? h('div', { class: 'error' }, e.error ?? 'Error') : null);
  }

  private async prepararModelo(zona: Zona): Promise<void> {
    this.modeloOcupado = true;
    this.modeloControl = new AbortController();
    const refrescar = () => this.render(this.ctx.almacen.estado);
    refrescar();
    try {
      this.modeloEstado = await prepararModelo(this.ctx, { tipo: 'zona', bbox: zona.bbox, epsg: zona.epsg }, {
        margenM: MARGEN_ZONA_M,
        senal: this.modeloControl.signal,
        forzar: this.modeloEstado?.estado === 'listo',
        onEstado: (e) => { this.modeloEstado = e; refrescar(); },
      });
      this.ctx.almacen.avisar('Modelo LiDAR de la zona listo.');
      await this.ctx.refrescarCandidatos();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg !== 'Cancelado') this.ctx.almacen.avisar(`LiDAR: ${msg}`, 'error');
    } finally {
      this.modeloOcupado = false;
      this.modeloControl = null;
      refrescar();
    }
  }

  private async verModelo(zona: Zona): Promise<void> {
    const e = this.modeloEstado;
    if (!e || e.estado !== 'listo') return;
    try {
      const { abrirEscenaModelo } = await import('../../lidar/escenaModelo');
      const clave = claveModelo({ bbox: zona.bbox, epsg: zona.epsg });
      await abrirEscenaModelo(this.ctx, e, { titulo: 'zona', clave, nombreFichero: `cernicalo-zona-${Math.round(zona.bbox[0])}-${Math.round(zona.bbox[1])}` });
    } catch (err) {
      this.ctx.almacen.avisar(`No se pudo abrir el modelo LiDAR: ${err instanceof Error ? err.message : String(err)}`, 'error');
    }
  }

  // ---------- Municipio ----------

  private async buscarMunicipio(): Promise<void> {
    const texto = this.textoMunicipio.trim();
    if (!texto) return;
    this.buscandoMunicipio = true;
    this.render(this.ctx.almacen.estado);
    try {
      this.candidatosMunicipio = await buscarMunicipios(texto);
      if (this.candidatosMunicipio.length === 1) await this.elegirMunicipio(this.candidatosMunicipio[0]);
    } catch (err) {
      this.candidatosMunicipio = null;
      this.ctx.almacen.avisar(`No se pudo consultar CartoCiudad: ${err instanceof Error ? err.message : String(err)}`, 'error');
    } finally {
      this.buscandoMunicipio = false;
      this.render(this.ctx.almacen.estado);
    }
  }

  private async elegirMunicipio(c: MunicipioCandidato): Promise<void> {
    try {
      const m = await poligonoMunicipio(c.codigo);
      const epsg = husoParaLonLat(m.lon || m.poligono[0][0][0][0], m.lat || m.poligono[0][0][0][1]);
      const poligono: MultiPoligono = m.poligono.map((pol) => pol.map((an) => proyectarAnillo(4326, epsg, an)));
      const zona: Zona = { bbox: bboxMultiPoligono(poligono), epsg, poligono, municipio: { codigo: m.codigo, nombre: m.nombre, provincia: m.provincia } };
      this.candidatosMunicipio = null;
      this.textoMunicipio = m.nombre;
      this.ctx.almacen.set({ zona, herramienta: 'ninguna' });
      this.ctx.mapa.setHerramienta('ninguna');
      this.ctx.mapa.mostrarZona(zona);
      this.ctx.mapa.encajarBbox(zona.bbox, epsg, 13);
    } catch (err) {
      this.ctx.almacen.avisar(`No se pudo obtener el límite de ${c.nombre}: ${err instanceof Error ? err.message : String(err)}`, 'error');
    }
  }

  /** La zona (con su limite municipal) se recuerda entre sesiones en localStorage; la base de datos ya persiste en OPFS. */
  private guardarZona(zona: Zona | null): void {
    try {
      if (zona) localStorage.setItem(CLAVE_ZONA, JSON.stringify(zona)); else localStorage.removeItem(CLAVE_ZONA);
    } catch { /* sin almacenamiento */ }
  }

  private restaurarZona(): void {
    if (this.ctx.almacen.estado.zona) return;
    try {
      const t = localStorage.getItem(CLAVE_ZONA);
      if (!t) return;
      const z = JSON.parse(t) as Zona;
      if (!Array.isArray(z.bbox) || z.bbox.length !== 4 || !z.epsg) return;
      this.ctx.almacen.set({ zona: z });
      if (z.municipio) this.textoMunicipio = z.municipio.nombre;
      void this.ctx.mapa.listo.then(() => this.ctx.mapa.mostrarZona(z));
    } catch { /* se ignora */ }
  }

  // ---------- Carga ----------

  private async cargar(): Promise<void> {
    const zona = this.ctx.almacen.estado.zona;
    if (!zona) return;
    const teselas = ZonaPanel.teselasDe(zona);
    const capasCatastro = [...this.capas].filter((c): c is CapaCatastro => c !== 'hy');
    const conAgua = this.capas.has('hy');
    const nHidro = conAgua ? teselasHidro(zona.bbox, zona.epsg).length : 0;
    let catastro: Progreso = { hechas: 0, total: teselas.length * capasCatastro.length, capa: null, tesela: null, registros: 0, errores: [], terminado: false, cancelado: false };
    this.ctx.almacen.set({ cargando: { ...catastro, total: catastro.total + nHidro } });
    try {
      const r = await this.ctx.db.cargarZona(teselas, capasCatastro, DIAS_VIGENCIA, CONCURRENCIA, (p) => { catastro = p; this.ctx.almacen.set({ cargando: { ...p, total: p.total + nHidro } }); });
      let texto = r.cancelado
        ? `Carga cancelada: ${r.descargadas} teselas descargadas.`
        : `Zona cargada: ${r.descargadas} teselas descargadas, ${r.reutilizadas} reutilizadas`
          + ` (${num(r.registros.cp)} parcelas, ${num(r.registros.bu)} edificios${r.registros.bp ? `, ${num(r.registros.bp)} partes` : ''}).`
          + (r.errores.length ? ` ${r.errores.length} tesela(s) con error.` : '');
      let erroresAgua = 0;
      if (conAgua && !r.cancelado) {
        const ra = await this.ctx.db.cargarHidrografia(zona.bbox, zona.epsg, DIAS_VIGENCIA,
          (p) => this.ctx.almacen.set({ cargando: { ...p, hechas: catastro.hechas + p.hechas, total: catastro.total + p.total, errores: [...catastro.errores, ...p.errores] } }));
        erroresAgua = ra.errores.length;
        texto += ra.cancelado
          ? ' Hidrografía cancelada.'
          : ` Ríos y arroyos: ${num(ra.cursos)} nuevos (${ra.reutilizadas} teselas de 5 km reutilizadas); distancia al agua calculada en ${num(ra.parcelasActualizadas)} parcelas.`
            + (erroresAgua ? ` ${erroresAgua} tesela(s) de hidrografía con error.` : '');
      }
      this.ctx.almacen.avisar(texto, (r.errores.length && !r.descargadas) || erroresAgua ? 'error' : 'info');
    } catch (err) {
      this.ctx.almacen.avisar(`No se pudo cargar la zona: ${err instanceof Error ? err.message : String(err)}`, 'error');
    } finally {
      this.ctx.almacen.set({ cargando: null });
      await this.ctx.refrescarResumen();
      await this.ctx.refrescarZona();
    }
  }

  private async vaciar(todo: boolean): Promise<void> {
    const msg = todo
      ? 'Se borrarán todos los datos del Catastro, los ríos, los candidatos y las búsquedas guardadas de este navegador. ¿Continuar?'
      : 'Se borrarán los datos del Catastro y los ríos cargados (los candidatos se conservan, pero sin sus datos catastrales hasta recargar la zona). ¿Continuar?';
    if (!confirm(msg)) return;
    if (todo) await this.ctx.db.vaciarTodo(); else await this.ctx.db.vaciarCatastro();
    this.ctx.almacen.set({ resultados: [], rcSeleccionado: null });
    this.ctx.mapa.setResultados([]);
    this.ctx.mapa.setSeleccion(null);
    await this.ctx.refrescarResumen();
    await this.ctx.refrescarCandidatos();
    await this.ctx.refrescarZona();
  }
}
